import { OpenDuration } from 'api'
import { clamp, cloneDefault } from './tools'

export const DURATION_DEF: OpenDuration = {
    open: 0,
    close: 0,
}

type Day = [OpenDuration, OpenDuration, OpenDuration, OpenDuration]
const DAY: Day = [DURATION_DEF, DURATION_DEF, DURATION_DEF, DURATION_DEF]

export type OpeningHours = [Day, Day, Day, Day, Day, Day, Day]
export const OPENING_HOURS_DEF: OpeningHours = [
    DAY,
    DAY,
    DAY,
    DAY,
    DAY,
    DAY,
    DAY,
]

export type UiDuration = null | OpenDuration
export type UiDay = [UiDuration, UiDuration, UiDuration, UiDuration]
export type UiOpeninHours = [UiDay, UiDay, UiDay, UiDay, UiDay, UiDay, UiDay]

export const UI_OPENING_HOURS_DEF: UiOpeninHours = normalizeOh(null)

export const sortOpeningHours = (oh: UiOpeninHours): OpeningHours => {
    const sortDay = (day: UiDay): Day => {
        return [...day]
            .map((d, i) => ({ d, i }))
            .sort((a, b) => {
                const aNull = a.d === null
                const bNull = b.d === null

                if (aNull !== bNull) return Number(aNull) - Number(bNull)

                if (aNull && bNull) return a.i - b.i

                const aHas = (a.d!.open ?? 0) > 0 || (a.d!.close ?? 0) > 0
                const bHas = (b.d!.open ?? 0) > 0 || (b.d!.close ?? 0) > 0

                // non-empty first
                if (aHas !== bHas) return Number(bHas) - Number(aHas)

                const ao = a.d!.open ?? 0
                const bo = b.d!.open ?? 0

                if (ao !== bo) return ao - bo

                // stable fallback
                return a.i - b.i
            })
            .map(x => x.d!) as Day
    }

    return oh.map(day => sortDay(day)) as OpeningHours
}

export function oh_server_parse(value: any): OpeningHours {
    let oh = cloneDefault(OPENING_HOURS_DEF)

    if (!value || typeof value != 'string') return oh

    try {
        let bs = atob(value)
        if (bs.length != 2 * DAY.length * OPENING_HOURS_DEF.length) {
            return oh
        }
        for (let i = 0; i < bs.length; i++) {
            let t = bs.charCodeAt(i)
            let day = ~~(i / 8)
            let dur = ~~(i / 2) % 4
            if (i % 2 == 0) {
                oh[day]![dur]!.open = t
            } else {
                oh[day]![dur]!.close = t
            }
        }
    } catch {
        return oh
    }

    oh = sortOpeningHours(oh)

    return oh
}

export function prepareOhForBackend(
    opening_hours: UiOpeninHours
): OpenDuration[][] {
    return opening_hours.map(day =>
        day.map(dur => {
            // Preserve empty row as-is
            if (!dur || (dur.open === 0 && dur.close === 0)) {
                return { open: 0, close: 0 }
            }

            // Add +1 safely and clamp to 97 (since indices go 0..96 and 97 is the "one-past" value)
            const open = Math.min(dur.open + 1, 97)
            const close = Math.min(dur.close + 1, 97)

            return { open, close }
        })
    )
}

const DAY_END = 96

function isValidDuration(dur: any): dur is OpenDuration {
    return (
        !!dur &&
        typeof dur.open === 'number' &&
        typeof dur.close === 'number' &&
        Number.isFinite(dur.open) &&
        Number.isFinite(dur.close)
    )
}

export function normalizeOh(
    opening_hours: string | null | undefined
): UiOpeninHours {
    const parsed = opening_hours
        ? oh_server_parse(opening_hours)
        : OPENING_HOURS_DEF

    const fixed = parsed.map(
        day =>
            day.map(dur => {
                if (
                    !dur ||
                    typeof dur.open !== 'number' ||
                    typeof dur.close !== 'number'
                ) {
                    return null
                }

                // backend uses 1..97
                if (dur.open <= 0 || dur.open > 97) return null
                if (dur.close <= 0 || dur.close > 97) return null

                const open = clamp(dur.open - 1, 0, 96)
                const close = clamp(dur.close - 1, 0, 96)

                return { open, close }
            }) as UiDay
    ) as UiOpeninHours

    return fixed.map(day => normalizeDayIntervals(day)) as UiOpeninHours
}

function normalizeDayIntervals(day: UiDay): UiDay {
    const intervals: OpenDuration[] = []

    for (const dur of day) {
        if (!isValidDuration(dur)) continue

        const open = clamp(Math.trunc(dur.open), 0, DAY_END)
        const close = clamp(Math.trunc(dur.close), 0, DAY_END)

        intervals.push({ open, close })
    }

    intervals.sort((a, b) => a.open - b.open || a.close - b.close)

    const merged: OpenDuration[] = []

    for (const current of intervals) {
        const prev = merged[merged.length - 1]

        // Merge overlap or direct touch:
        // [0,2] + [2,6] -> [0,6]
        // [10,20] + [18,30] -> [10,30]
        if (prev && current.open <= prev.close) {
            prev.close = Math.max(prev.close, current.close)
            continue
        }

        merged.push({ ...current })
    }

    // Merge across midnight:
    // [24,96] + [0,2] -> [24,2]
    if (merged.length > 1) {
        const first = merged[0]
        const last = merged[merged.length - 1]

        if (first!.open === 0 && last!.close === DAY_END) {
            const wrapped: OpenDuration = {
                open: last!.open,
                close: first!.close,
            }

            const middle = merged.slice(1, -1)
            merged.length = 0
            merged.push(...middle, wrapped)
        }
    }

    const out: UiDay = [null, null, null, null]

    for (let i = 0; i < merged.length && i < 4; i++) {
        const dur = merged[i]

        if (!dur) continue

        out[i] = dur
    }

    return out
}
